import React, { useCallback, useEffect, useState } from "react";
import { FiCheckCircle, FiUploadCloud, FiSearch, FiShield } from "react-icons/fi";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import DeploymentDiffModal, { type DiffModalItem } from "../../Components/Deployment/DeploymentDiffModal";
import { client } from "../../Clients";
import { DEFAULT_LANGUAGE, PAGE_CONTENT_TYPE } from "./deploymentUtils";
import { usePagesOutOfSync, useDeploymentQueue, useDeploymentAnalysis } from "./useDeploymentHooks";
import DeploymentQueueList from "./DeploymentQueueList";
import AnalysisList from "./AnalysisList";

const DeploymentPanel: React.FC<{
	title: string;
	target: "preview" | "production";
	deployLabel: string;
	onDeploy: () => void;
	deploying: boolean;
	disableDeploy?: boolean;
	/** When this changes after a deploy, re-run analysis so the list reflects in-sync skip. */
	refreshAfterDeployTrigger?: number;
	/** When this changes (preview was deployed), reload so production panel shows up-to-date pending list. */
	refreshAfterPreviewDeployTrigger?: number;
	/** Called when this panel starts or stops analyzing (so parent can disable the other panel's Analyze). */
	onAnalyzingChange?: (analyzing: boolean) => void;
	/** When true, Analyze button is disabled (other panel is analyzing). */
	disableAnalyze?: boolean;
	/** When true, the panel's own deploy button is hidden — the parent
	 *  (the tabbed deployment view) owns the primary action in its status hero. */
	hideDeploy?: boolean;
	/** When true, the panel's big title/shield is hidden — the parent already
	 *  shows the environment in its tab + hero. */
	hideTitle?: boolean;
}> = ({ title, target, deployLabel, onDeploy, deploying, disableDeploy, refreshAfterDeployTrigger, refreshAfterPreviewDeployTrigger, onAnalyzingChange, disableAnalyze, hideDeploy, hideTitle }) => {
	const { items: queueItems, loading: queueLoading, error: queueError, load: loadQueue } = useDeploymentQueue(target);
	const { items: outOfSyncItems, loading: outOfSyncLoading, load: loadOutOfSync } = usePagesOutOfSync(target);
	const { pageAnalyses, analyzing, analyzed, analyze } = useDeploymentAnalysis(target);
	const [showDiffModal, setShowDiffModal] = useState(false);
	const [initialDiffItem, setInitialDiffItem] = useState<DiffModalItem | null>(null);
	const [orphanPaths, setOrphanPaths] = useState<string[]>([]);
	const [orphanLoading, setOrphanLoading] = useState(false);
	const [orphanError, setOrphanError] = useState<string | null>(null);
	const [showOrphans, setShowOrphans] = useState(false);
	const hasErrors = pageAnalyses.some((pa) => pa.issues.some((i) => i.severity === "error"));
	const deployDisabled = disableDeploy ?? false;

	useEffect(() => {
		onAnalyzingChange?.(analyzing);
	}, [analyzing, onAnalyzingChange]);

	const handleAnalyzeStorage = useCallback(async () => {
		setOrphanLoading(true);
		setOrphanError(null);
		try {
			const response = await client.deployment.apiDeploymentPageStorageOrphansGet(target);
			setOrphanPaths(response.data ?? []);
			setShowOrphans(true);
		} catch (err: any) {
			setOrphanError(err?.response?.data ?? err?.message ?? "Storage analysis failed.");
			setShowOrphans(true);
		} finally {
			setOrphanLoading(false);
		}
	}, [target]);

	useEffect(() => {
		loadQueue();
		loadOutOfSync();
	}, [loadQueue, loadOutOfSync]);

	useEffect(() => {
		if (typeof refreshAfterDeployTrigger === "number" && refreshAfterDeployTrigger > 0) {
			loadQueue();
			loadOutOfSync();
		}
	}, [refreshAfterDeployTrigger, loadQueue, loadOutOfSync]);

	/** When preview is deployed (single page or all), production panel reloads Changes only (analyze stays manual). */
	useEffect(() => {
		if (typeof refreshAfterPreviewDeployTrigger !== "number" || refreshAfterPreviewDeployTrigger <= 0) return;
		const bust = Date.now();
		const t = setTimeout(() => { loadQueue(bust); loadOutOfSync(bust); }, 500);
		return () => clearTimeout(t);
	}, [refreshAfterPreviewDeployTrigger, loadQueue, loadOutOfSync]);

	const queueDiffItems: DiffModalItem[] = queueItems.map((item) => {
		const contentType = item.contentType ?? "";
		const language = item.language ?? DEFAULT_LANGUAGE;
		const slug = item.slug ?? item.contentId ?? "";
		if (contentType && contentType.toLowerCase() !== PAGE_CONTENT_TYPE && !slug) {
			return { contentType, language };
		}
		return { slug, language, title: item.message ?? null };
	});
	const pageDiffItems: DiffModalItem[] = pageAnalyses.map((pa) => ({
		id: pa.id ?? undefined,
		slug: pa.slug,
		language: pa.language,
		title: pa.title
	}));
	const diffMap = new Map<string, DiffModalItem>();
	const diffKey = (item: DiffModalItem) =>
		"slug" in item ? `page:${item.language}/${item.slug}` : `singleton:${item.contentType}/${item.language}`;
	[...queueDiffItems, ...pageDiffItems].forEach((item) => {
		const key = diffKey(item);
		if (!diffMap.has(key)) diffMap.set(key, item);
	});
	const diffModalItems = Array.from(diffMap.values());
	/** Show in Analysis only pages that are out of sync or have issues, so reverted pages disappear. */
	const pageAnalysesToShow = pageAnalyses.filter(
		(pa) => pa.issues.length > 0 || outOfSyncItems.some((i) => i.slug === pa.slug && i.language === pa.language)
	);

	const isBusy = queueLoading || outOfSyncLoading || analyzing;
	const pendingCount = queueItems.filter(i => (i.status ?? "").toLowerCase() === "pending").length;
	const failedCount = queueItems.filter(i => (i.status ?? "").toLowerCase() === "failed").length;
	const processingCount = queueItems.filter(i => (i.status ?? "").toLowerCase() === "processing").length;
	const isProduction = target === "production";

	const deployBtnClass = isProduction
		? "inline-flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-800 shadow-sm transition hover:border-orange-400 hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-60"
		: "inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm transition hover:border-emerald-400 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60";

	return (
		<div className="relative flex h-full min-h-0 w-full flex-col rounded-2xl border border-slate-200 bg-slate-50/50 shadow-sm">
			{isBusy && (
				/* Solid surface (no backdrop blur). A spinner + label is the right affordance here
				   because analyse/deploy are active operations in progress, not a first-paint load. */
				<div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-card">
					<div className="flex flex-col items-center gap-4">
						<LoadingSpinner size="lg" />
						<p className="text-sm font-medium text-muted-foreground">
							{analyzing ? "Analyse en cours…" : deploying ? "Déploiement en cours…" : "Chargement…"}
						</p>
					</div>
				</div>
			)}
			<header className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
				<div className="flex items-center justify-between gap-3">
					{hideTitle ? (
						<div className="text-sm font-bold uppercase tracking-wide text-slate-500">Changements &amp; analyse</div>
					) : (
						<div className="flex items-center gap-3">
							<h2 className="text-lg font-bold text-slate-900">{title}</h2>
							{isProduction && <FiShield className="h-4 w-4 text-orange-500" title="Production environment" />}
						</div>
					)}
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={() => analyze()}
							disabled={analyzing || disableAnalyze}
							className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{analyzing ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-4 w-4" />}
							Analyze links
						</button>
						<button
							type="button"
							onClick={handleAnalyzeStorage}
							disabled={orphanLoading || analyzing}
							className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{orphanLoading ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-4 w-4" />}
							Analyze storage
						</button>
						{!hideDeploy && (
							<button
								type="button"
								onClick={onDeploy}
								disabled={deployDisabled || deploying || analyzing || queueItems.length === 0}
								className={deployBtnClass}
							>
								{deploying ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-4 w-4" />}
								{deployLabel}
							</button>
						)}
					</div>
				</div>
				{/* Summary counter bar */}
				<div className="mt-3 flex items-center gap-3 text-xs">
					{pendingCount > 0 && (
						<span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 font-medium text-sky-700">
							<span className="inline-flex h-1.5 w-1.5 rounded-full bg-sky-500" />
							{pendingCount} pending
						</span>
					)}
					{processingCount > 0 && (
						<span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 font-medium text-amber-700">
							<span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
							{processingCount} processing
						</span>
					)}
					{failedCount > 0 && (
						<span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 font-medium text-rose-700">
							<span className="inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
							{failedCount} failed
						</span>
					)}
					{queueItems.length === 0 && !queueLoading && (
						<span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 font-medium text-emerald-700">
							<FiCheckCircle className="h-3 w-3" /> All up to date
						</span>
					)}
					{outOfSyncItems.length > 0 && (
						<span className="text-slate-500">{outOfSyncItems.length} page{outOfSyncItems.length !== 1 ? "s" : ""} out of sync</span>
					)}
				</div>
			</header>
			<main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-6">
				{showOrphans && (
					<section>
						<h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">
							Storage analysis
						</h3>
						<p className="text-sm text-slate-600 mb-2">
							Files present in {title.toLowerCase()} storage that do not match any current page record. No files are deleted.
						</p>
						{!orphanLoading && !orphanError && (
							<p className="text-xs text-slate-500 mb-2">
								{orphanPaths.length === 0
									? "0 orphaned files detected."
									: `${orphanPaths.length} orphaned file${orphanPaths.length === 1 ? "" : "s"} detected.`}
							</p>
						)}
						{orphanError && <p className="text-sm text-rose-600 mb-2">{orphanError}</p>}
						{orphanLoading && (
							<div className="flex items-center gap-2 text-slate-500 text-sm">
								<LoadingSpinner size="sm" /> Analyzing storage…
							</div>
						)}
						{!orphanLoading && orphanPaths.length === 0 && !orphanError && (
							<p className="text-sm text-emerald-600">No orphaned page files detected.</p>
						)}
						{orphanPaths.length > 0 && (
							<ul className="space-y-1 text-xs font-mono text-slate-600">
								{orphanPaths.map((path) => (
									<li key={path} className="rounded-md border border-slate-200 bg-white px-2 py-1">
										{path}
									</li>
								))}
							</ul>
						)}
					</section>
				)}
				<section>
					<h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">Changes</h3>
					<DeploymentQueueList
						items={queueItems}
						loading={queueLoading}
						error={queueError}
						onLoad={loadQueue}
						onViewChanges={(item) => {
							const contentType = item.contentType ?? "";
							const language = item.language ?? DEFAULT_LANGUAGE;
							const slug = item.slug ?? item.contentId ?? "";
							const diffItem =
								contentType && contentType.toLowerCase() !== PAGE_CONTENT_TYPE && !slug
									? { contentType, language }
									: { slug, language, title: item.message ?? null };
							setInitialDiffItem(diffItem);
							setShowDiffModal(true);
						}}
					/>
				</section>
				<section>
					<h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-2">Link analysis</h3>
					<AnalysisList
						pageAnalyses={pageAnalysesToShow}
						analyzing={analyzing}
						analyzed={analyzed}
						hasErrors={hasErrors}
						targetLabel={title}
						pendingItems={outOfSyncItems}
						onViewChanges={(pa) => {
							setInitialDiffItem({ id: pa.id ?? undefined, slug: pa.slug, language: pa.language, title: pa.title });
							setShowDiffModal(true);
						}}
						onDeploySingle={undefined}
						deployingSlugLanguage={null}
						disableDeploySingle={true}
					/>
				</section>
			</main>
			<DeploymentDiffModal
				show={showDiffModal}
				onClose={() => { setShowDiffModal(false); setInitialDiffItem(null); }}
				items={diffModalItems}
				initialItem={initialDiffItem}
			/>
		</div>
	);
};

export default DeploymentPanel;
